import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Moment } from 'moment';
import { RootState } from '../store/store';
import { exceedsMaxStay } from '../utils/hotelStayLimit';
import { showHotelSearchError } from '../utils/hotelSearchError';
import { RATEHAWK_API_LIMIT_VALUES } from '../utils/constants';

/**
 * Guard for hotel/DP search hooks: returns true when the requested stay is
 * longer than the Ratehawk 30-night limit, so callers can keep the query
 * disabled (dates may come from URL tampering / deep links that bypass the
 * pickers). Also surfaces the translated `maxStayExceededError` toast once
 * per violation.
 *
 * `dateFormat` defaults to the site date format; pass 'YYYY-MM-DD' for
 * already-normalized API params.
 */
const useMaxStayLimitGuard = (
  from: string | Moment | null | undefined,
  to: string | Moment | null | undefined,
  dateFormat?: string
): boolean => {
  const { siteConfig } = useSelector((data: RootState) => data.config.config);
  const { searchDatePicker: t } = useSelector(
    (data: RootState) => data.config.translations
  );

  const stayTooLong = exceedsMaxStay(
    from,
    to,
    dateFormat ?? siteConfig?.dateFormat
  );

  useEffect(() => {
    if (stayTooLong) {
      showHotelSearchError(
        t?.maxStayExceededError ||
          `Hotel stays are limited to ${RATEHAWK_API_LIMIT_VALUES.max_days} nights`
      );
    }
  }, [stayTooLong, t?.maxStayExceededError]);

  return stayTooLong;
};

export default useMaxStayLimitGuard;
